import type { RequirementSection } from '@ispot/shared'
import type { Group } from '@/hooks/useGroupedRequirements'
import { GroupAvatar } from '../GroupAvatar'
import { HoverInsertZone } from './HoverInsertZone'
import { NotesNode } from './NotesNode'
import type { NotesSharedProps } from './notesShared'

function sectionFromGroupId(id: string): RequirementSection | undefined {
  if (!id.startsWith('section:')) return undefined
  const s = id.slice('section:'.length)
  if (s === 'overview' || s === 'functional' || s === 'non-functional') return s
  return undefined
}

/** One persona (or status/priority/theme/section) group rendered as a chapter. */
export function NotesChapter({ group: g, ...shared }: NotesSharedProps & { group: Group }) {
  const hasEpics = g.roots.length > 0
  const isSection = g.id.startsWith('section:')

  return (
    <section
      className={isSection ? 'pt-10 mt-14 first:mt-0 first:pt-4' : 'pt-5 mt-9 first:border-t-0 first:pt-2 first:mt-0'}
      style={{ borderTop: '1px solid var(--color-border)' }}
    >
      {isSection ? (
        // Editorial section heading: typography only, no avatar.
        <div className="pb-4 mb-3">
          <div
            className="font-mono mb-2"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-fg-muted)',
            }}
          >
            Section
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1
              className="m-0"
              style={{
                fontFamily: "'Fraunces', 'DM Sans', serif",
                fontSize: 32, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.1,
                color: 'var(--color-fg)',
              }}
            >
              {g.label}
            </h1>
            <span className="text-[13px]" style={{ color: 'var(--color-fg-muted)' }}>
              {g.sub && <>{g.sub} · </>}{g.storyCount} {g.storyCount === 1 ? 'story' : 'stories'}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="sticky top-0 flex items-center gap-3 pb-2.5 z-10"
          style={{ background: 'linear-gradient(var(--color-warm-50) 60%, rgba(252,252,249,0))' }}
        >
          <GroupAvatar size={32} photo={g.photo} initials={g.initials} color={g.color} muted={g.unassigned} />
          <div className="flex-1 min-w-0 flex items-baseline gap-3 flex-wrap">
            <h1
              className="m-0"
              style={{
                fontFamily: "'Fraunces', 'DM Sans', serif",
                fontSize: 22, fontWeight: 500, letterSpacing: '-0.012em', lineHeight: 1.2,
                color: g.unassigned ? 'var(--color-fg-secondary)' : 'var(--color-fg)',
              }}
            >
              {g.label}
            </h1>
            <span className="text-[12px]" style={{ color: 'var(--color-fg-muted)' }}>
              {g.sub && <>{g.sub} · </>}{g.storyCount} {g.storyCount === 1 ? 'story' : 'stories'}
            </span>
          </div>
        </div>
      )}

      {g.roots.map(node => <NotesNode key={node.key} node={node} {...shared} />)}

      <HoverInsertZone
        defaultType="epic"
        parents={{}}
        personaSessionId={shared.personaSessionId}
        section={sectionFromGroupId(g.id)}
        onCreate={shared.onCreate}
        emptyHint={hasEpics ? undefined : 'Add epic'}
      />
    </section>
  )
}
